import { applicationInfoSchema, applicationSlugSchema, deviceAuthorizationContract, deviceConsentContract, deviceVerifyContract, tokenDeviceCodeInputSchema } from "./deviceCode.mjs";
import { z } from "zod";
import { oc } from "@orpc/contract";
//#region src/api/orpc/oauth2/index.ts
const tokenTypeHintSchema = z.enum(["access_token", "refresh_token"]).optional();
const authorizeInputSchema = applicationSlugSchema.extend({
	response_type: z.literal("code"),
	client_id: z.string(),
	redirect_uri: z.url(),
	scope: z.string().optional().meta({ description: "Space-separated list of scopes" }),
	state: z.string().optional(),
	code_challenge: z.string(),
	code_challenge_method: z.literal("S256")
});
const authorizeNeedsConsentSchema = z.object({
	auto_consented: z.literal(false),
	consent_token: z.string(),
	client_id: z.string(),
	application: applicationInfoSchema,
	scopes: z.array(z.string())
});
const authorizeAutoConsentedSchema = z.object({
	auto_consented: z.literal(true),
	code: z.string(),
	state: z.string().optional(),
	redirect_uri: z.url()
});
const authorizeOutputSchema = z.discriminatedUnion("auto_consented", [authorizeNeedsConsentSchema, authorizeAutoConsentedSchema]);
const consentInputSchema = applicationSlugSchema.extend({
	consent_token: z.string(),
	approved: z.boolean(),
	remember: z.boolean().optional()
});
const consentApprovedSchema = z.object({
	approved: z.literal(true),
	code: z.string(),
	state: z.string().optional(),
	redirect_uri: z.url()
});
const consentDeniedSchema = z.object({
	approved: z.literal(false),
	error: z.literal("access_denied"),
	state: z.string().optional(),
	redirect_uri: z.url()
});
const consentOutputSchema = z.discriminatedUnion("approved", [consentApprovedSchema, consentDeniedSchema]);
const tokenAuthCodeInputSchema = applicationSlugSchema.extend({
	grant_type: z.literal("authorization_code"),
	code: z.string(),
	redirect_uri: z.url(),
	code_verifier: z.string()
});
const tokenRefreshInputSchema = applicationSlugSchema.extend({
	grant_type: z.literal("refresh_token"),
	refresh_token: z.string()
});
const tokenClientCredentialsInputSchema = applicationSlugSchema.extend({
	grant_type: z.literal("client_credentials"),
	scope: z.string().optional().meta({ description: "Space-separated list of M2M scopes" })
});
const tokenInputSchema = z.discriminatedUnion("grant_type", [
	tokenAuthCodeInputSchema,
	tokenRefreshInputSchema,
	tokenDeviceCodeInputSchema,
	tokenClientCredentialsInputSchema
]);
const tokenOutputSchema = z.object({
	access_token: z.string(),
	token_type: z.literal("Bearer"),
	expires_in: z.number().int(),
	refresh_token: z.string().optional(),
	scope: z.string().optional()
});
const tokenLookupInputSchema = applicationSlugSchema.extend({
	token: z.string(),
	token_type_hint: tokenTypeHintSchema
});
const introspectActiveSchema = z.object({
	active: z.literal(true),
	sub: z.string(),
	client_id: z.string(),
	token_type: z.string(),
	scope: z.string(),
	exp: z.number().int(),
	iat: z.number().int()
});
const introspectInactiveSchema = z.object({ active: z.literal(false) });
const introspectOutputSchema = z.discriminatedUnion("active", [introspectActiveSchema, introspectInactiveSchema]);
const serverMetadataOutputSchema = z.object({
	issuer: z.url(),
	authorization_endpoint: z.url(),
	token_endpoint: z.url(),
	device_authorization_endpoint: z.url().optional(),
	revocation_endpoint: z.url(),
	introspection_endpoint: z.url(),
	scopes_supported: z.array(z.string()),
	response_types_supported: z.array(z.string()),
	grant_types_supported: z.array(z.string()),
	code_challenge_methods_supported: z.array(z.string()),
	token_endpoint_auth_methods_supported: z.array(z.string())
});
const badRequestError = { BAD_REQUEST: {
	status: 400,
	message: "Bad request"
} };
const oauthTokenErrors = {
	invalid_grant: {
		status: 400,
		message: "The provided grant is invalid, expired, or revoked"
	},
	invalid_client: {
		status: 401,
		message: "Invalid client credentials"
	},
	authorization_pending: {
		status: 400,
		message: "The user has not yet completed authorization"
	},
	slow_down: {
		status: 400,
		message: "Polling too frequently"
	},
	expired_token: {
		status: 400,
		message: "Device code has expired"
	},
	access_denied: {
		status: 400,
		message: "The user denied the authorization request"
	}
};
const oauth2Contract = oc.prefix("/oauth2").tag("OAuth2").router({
	authorize: oc.route({
		summary: "OAuth2 authorization request",
		description: "Validate OAuth2 request parameters and return client info for the consent UI. Auth: requires authenticated user session (cookie).",
		method: "GET",
		path: "/{applicationSlug}/authorize"
	}).input(authorizeInputSchema).output(authorizeOutputSchema).errors(badRequestError),
	consent: oc.route({
		summary: "OAuth2 consent decision",
		description: "Record user consent and issue an authorization code if approved. Auth: requires authenticated user session (cookie).",
		method: "POST",
		path: "/{applicationSlug}/consent"
	}).input(consentInputSchema).output(consentOutputSchema).errors(badRequestError),
	token: oc.route({
		summary: "OAuth2 token exchange",
		description: "Exchange an authorization code for tokens, or refresh an access token. Auth: HTTP Basic (Authorization: Basic base64(client_id:client_secret)).",
		method: "POST",
		path: "/{applicationSlug}/token"
	}).input(tokenInputSchema).output(tokenOutputSchema).errors(oauthTokenErrors),
	revoke: oc.route({
		summary: "Revoke a token",
		description: "Invalidate an access or refresh token. Always returns 200 per RFC 7009. Auth: HTTP Basic (Authorization: Basic base64(client_id:client_secret)).",
		method: "POST",
		path: "/{applicationSlug}/revoke"
	}).input(tokenLookupInputSchema).output(z.object({})).errors({ invalid_client: oauthTokenErrors.invalid_client }),
	introspect: oc.route({
		summary: "Introspect a token",
		description: "Check whether a token is active and retrieve its metadata. Auth: HTTP Basic (Authorization: Basic base64(client_id:client_secret)).",
		method: "POST",
		path: "/{applicationSlug}/introspect"
	}).input(tokenLookupInputSchema).output(introspectOutputSchema).errors({ invalid_client: oauthTokenErrors.invalid_client }),
	serverMetadata: oc.route({
		summary: "OAuth2 server metadata",
		description: "OAuth2 server configuration and endpoint discovery. Auth: none (public).",
		method: "GET",
		path: "/{applicationSlug}/.well-known/oauth-authorization-server"
	}).input(applicationSlugSchema).output(serverMetadataOutputSchema),
	deviceAuthorization: deviceAuthorizationContract,
	deviceVerify: deviceVerifyContract,
	deviceConsent: deviceConsentContract
});
//#endregion
export { oauth2Contract };
